// src/pages/SaleInvoiceDetail.jsx
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import {
  ArrowLeft, ChevronLeft, ChevronRight, Plus, Trash2, Save,
  Pencil, Download, Receipt, AlertCircle, X, Check, RefreshCw,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { saleInvoicesAPI } from '../services/api';
import './SaleInvoiceDetail.css';

// ── Helpers ──────────────────────────────────────────────────────────────────
const fmtNum = (n) => Number(n || 0).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtDate = (d) => d ? new Date(d).toLocaleDateString('fr-FR') : '—';
const fmtDateTime = (d) => d ? `${new Date(d).toLocaleDateString('fr-FR')} ${new Date(d).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}` : '—';
const toInput = (d) => d ? d.slice(0, 10) : '';

const STATUTS_INVOICE = ['NEW', 'EN_ATTENTE', 'VALIDEE', 'PAYEE', 'ANNULEE'];
const TYPES_INVOICE = ['FACTURE_VENTE', 'AVOIR', 'PROFORMA'];
const DEVISES = [
  { value: 'EUR', label: 'EUR — Euro' },
  { value: 'CHF', label: 'CHF — Franc suisse' },
  { value: 'DZD', label: 'DZD — Dinar algérien' },
  { value: 'TND', label: 'TND — Dinar tunisien' },
  { value: 'MAD', label: 'MAD — Dirham marocain' },
  { value: 'USD', label: 'USD — Dollar américain' },
  { value: 'GBP', label: 'GBP — Livre sterling' },
];
const statutColor = (s) => ({
  NEW:        { color: '#6366F1', bg: 'rgba(99,102,241,0.12)' },
  EN_ATTENTE: { color: '#F59E0B', bg: 'rgba(245,158,11,0.12)' },
  VALIDEE:    { color: '#3B82F6', bg: 'rgba(59,130,246,0.12)' },
  PAYEE:      { color: '#16A34A', bg: 'rgba(34,197,94,0.12)' },
  ANNULEE:    { color: '#EF4444', bg: 'rgba(239,68,68,0.12)' },
}[s] || { color: '#8B8FA8', bg: 'rgba(139,143,168,0.12)' });

const EMPTY_LINE = { description: '', categorie: '', projet_ref: '', pu: '', quantite: '1', taux_tva: '20' };

const fmtInput = (n) => (Number(n || 0)).toFixed(2);

function SLabel({ children }) {
  return <div className="sid-section-title">{children}</div>;
}
function Field({ label, value, children }) {
  return (
    <div className="sid-field-row">
      <div className="sid-field-label">{label}</div>
      <div className="sid-field-value">{children ?? (value || '—')}</div>
    </div>
  );
}
function EditField({ label, children }) {
  return (
    <div className="sid-field-row">
      <div className="sid-field-label">{label}</div>
      <div className="sid-field-value">{children}</div>
    </div>
  );
}

// ── Main ─────────────────────────────────────────────────────────────────────
export default function SaleInvoiceDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isNew = id === 'new';
  const preselectedProjetId = searchParams.get('projet_id');

  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving]   = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [issuing, setIssuing] = useState(false);
  const [activeTab, setActiveTab] = useState('apercu');

  // Formulaire header
  const EMPTY_FORM = {
    type_facture: 'FACTURE_VENTE', description: '', emetteur: '', destinataire: '',
    message_facture: '', date_facture: '', date_echeance: '', date_paiement: '',
    total_ht: '', total_ttc: '', net_a_payer: '', montant_paye: '0',
    statut: 'NEW', currency: 'EUR', affacturage: false, adresse_sur_facture: '',
    code_client: '', methode_reglement: '', date_reglement: '',
    centre_cout: '', bon_commande: '', n_contrat_ref: '', n_chrono_devis: '',
    charge_affaires: '', charges_affaires_pct: '', entite_commerciale: '', methode_paiement_rib: '',
    projet_id: preselectedProjetId || null,
    lines: [],
  };
  const [form, setForm]   = useState(EMPTY_FORM);
  const [editMode, setEditMode] = useState(isNew);

  // Lignes
  const [lines, setLines]         = useState([]);
  const [showLineForm, setShowLineForm] = useState(false);
  const [lineForm, setLineForm]   = useState({ ...EMPTY_LINE });
  const [editLineId, setEditLineId] = useState(null);
  const [savingLine, setSavingLine] = useState(false);

  const [defaults, setDefaults] = useState(null);

  const loadDefaults = useCallback(async () => {
    if (!isNew) return;
    try {
      const params = {};
      const projetId = searchParams.get('projet_id');
      const entityId = searchParams.get('legal_entity_id');
      if (projetId) params.projet_id = projetId;
      if (entityId) params.legal_entity_id = entityId;
      const { data } = await saleInvoicesAPI.defaults(params);
      setDefaults(data);
      setForm((f) => {
        const next = { ...f };
        Object.entries(data).forEach(([key, value]) => {
          if (key in next && (next[key] === '' || next[key] === null || next[key] === undefined)) {
            next[key] = value;
          }
        });
        return next;
      });
      if (data.taux_tva != null) {
        setLineForm((l) => ({ ...l, taux_tva: String(data.taux_tva) }));
      }
    } catch {
      toast.error('Impossible de charger les valeurs par défaut');
    }
  }, [isNew, searchParams]);

  useEffect(() => { loadDefaults(); }, [loadDefaults]);

  const load = useCallback(async () => {
    if (isNew) return;
    setLoading(true);
    try {
      const r = await saleInvoicesAPI.get(id);
      const inv = r.data;
      setInvoice(inv);
      setLines(inv.lines || []);
      setForm({
        type_facture:     inv.type_facture || 'FACTURE_VENTE',
        description:      inv.description || '',
        emetteur:         inv.emetteur || '',
        destinataire:     inv.destinataire || '',
        message_facture:  inv.message_facture || '',
        date_facture:     toInput(inv.date_facture),
        date_echeance:    toInput(inv.date_echeance),
        date_paiement:    toInput(inv.date_paiement),
        total_ht:         inv.total_ht ?? '',
        total_ttc:        inv.total_ttc ?? '',
        net_a_payer:      inv.net_a_payer ?? '',
        montant_paye:     inv.montant_paye ?? '0',
        statut:           inv.statut || 'NEW',
        currency:         inv.currency || 'EUR',
        affacturage:      inv.affacturage || false,
        adresse_sur_facture: inv.adresse_sur_facture || '',
        code_client:      inv.code_client || '',
        methode_reglement: inv.methode_reglement || '',
        date_reglement:   toInput(inv.date_reglement),
        centre_cout:      inv.centre_cout || '',
        bon_commande:     inv.bon_commande || '',
        n_contrat_ref:    inv.n_contrat_ref || '',
        n_chrono_devis:   inv.n_chrono_devis || '',
        charge_affaires:  inv.charge_affaires || '',
        charges_affaires_pct: inv.charges_affaires_pct ?? '',
        entite_commerciale: inv.entite_commerciale || '',
        methode_paiement_rib: inv.methode_paiement_rib || '',
        lines: [],
      });
    } catch {
      toast.error('Erreur chargement facture');
    } finally {
      setLoading(false);
    }
  }, [id, isNew]);

  useEffect(() => { load(); }, [load]);

  // ── Save principal ───────────────────────────────────────────────────────
  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        ...form,

        montant_paye: form.montant_paye !== '' ? parseFloat(form.montant_paye) : 0,
        charges_affaires_pct: form.charges_affaires_pct !== '' ? parseFloat(form.charges_affaires_pct) : null,
        date_facture:  form.date_facture || null,
        date_echeance: form.date_echeance || null,
        date_paiement: form.date_paiement || null,
        date_reglement: form.date_reglement || null,
      };
      if (isNew) {
        payload.lines = lines.map(l => ({
          description: l.description, categorie: l.categorie, projet_ref: l.projet_ref,
          pu: parseFloat(l.pu || 0), quantite: parseFloat(l.quantite || 1), taux_tva: parseFloat(l.taux_tva || 20),
        }));
        payload.legal_entity_id = defaults?.legal_entity_id || null;
        payload.company_id      = defaults?.company_id || null;
        payload.projet_id       = defaults?.projet_id || null;
        payload.currency        = defaults?.currency || 'EUR';
      } else {
        delete payload.lines;
      }
      delete payload.total_ht;
      delete payload.total_ttc;
      delete payload.net_a_payer;
      if (isNew) {
        const r = await saleInvoicesAPI.create(payload);
        toast.success(`Facture ${r.data.ref} créée`);
        navigate(`/sale-invoices/${r.data.id}`);
      } else {
        await saleInvoicesAPI.update(id, payload);
        toast.success('Facture mise à jour');
        setEditMode(false);
        load();
      }
    } catch {
      toast.error('Erreur enregistrement');
    } finally {
      setSaving(false);
    }
  };

  // ── Lignes ───────────────────────────────────────────────────────────────
  const computedLine = (l) => {
    const pu  = parseFloat(l.pu || 0);
    const qty = parseFloat(l.quantite || 1);
    const tva = parseFloat(l.taux_tva || 0) / 100;
    const total = pu * qty;
    return { pu, qty, tva: tva * 100, tvaAmt: total * tva, total, totalTtc: total * (1 + tva), puTtc: pu * (1 + tva) };
  };

  const handleSaveLine = async () => {
    setSavingLine(true);
    try {
      const payload = {
        description: lineForm.description, categorie: lineForm.categorie,
        projet_ref: lineForm.projet_ref,
        pu: parseFloat(lineForm.pu || 0),
        quantite: parseFloat(lineForm.quantite || 1),
        taux_tva: parseFloat(lineForm.taux_tva || 20),
      };
      if (isNew) {
        if (editLineId !== null) {
          setLines(ls => ls.map((l, i) => i === editLineId ? payload : l));
        } else {
          setLines(ls => [...ls, payload]);
        }
        setShowLineForm(false); setEditLineId(null); setLineForm({ ...EMPTY_LINE });
      } else {
        if (editLineId) {
          await saleInvoicesAPI.updateLine(id, editLineId, payload);
        } else {
          await saleInvoicesAPI.addLine(id, payload);
        }
        toast.success('Ligne enregistrée');
        setShowLineForm(false); setEditLineId(null); setLineForm({ ...EMPTY_LINE });
        load();
      }
    } catch {
      toast.error('Erreur ligne');
    } finally {
      setSavingLine(false);
    }
  };

  const handleDeleteLine = async (lineId, idx) => {
    if (!window.confirm('Supprimer cette ligne ?')) return;
    if (isNew) {
      setLines(ls => ls.filter((_, i) => i !== idx));
    } else {
      try {
        await saleInvoicesAPI.deleteLine(id, lineId);
        toast.success('Ligne supprimée');
        load();
      } catch { toast.error('Erreur suppression'); }
    }
  };

  const startEditLine = (line, idxOrId) => {
    setLineForm({
      description: line.description || '', categorie: line.categorie || '',
      projet_ref: line.projet_ref || '', pu: String(line.pu || ''),
      quantite: String(line.quantite || '1'), taux_tva: String(line.taux_tva || '20'),
    });
    setEditLineId(idxOrId);
    setShowLineForm(true);
  };

  // ── Totaux lignes ────────────────────────────────────────────────────────
  const totalHT  = lines.reduce((s, l) => s + computedLine(l).total, 0);
  const totalTVA = lines.reduce((s, l) => s + computedLine(l).tvaAmt, 0);
  const totalTTC = lines.reduce((s, l) => s + computedLine(l).totalTtc, 0);

  // ── Statut changement rapide ────────────────────────────────────────────
  const handleDownload = async (kind) => {
    setDownloading(true);
    try {
      const res = kind === 'pdf'
        ? await saleInvoicesAPI.downloadPdf(id)
        : await saleInvoicesAPI.downloadXml(id);
      const url = window.URL.createObjectURL(res.data);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${inv?.ref || 'facture'}.${kind}`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(err?.response?.status === 503
        ? 'Le moteur de rendu PDF n’est pas disponible sur le serveur'
        : 'Téléchargement impossible');
    } finally {
      setDownloading(false);
    }
  };

  const handleIssue = async () => {
    setIssuing(true);
    try {
      await saleInvoicesAPI.issue(id);
      toast.success('Facture émise');
      load();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (detail?.issues) {
        toast.error(`${detail.issues.length} mention(s) obligatoire(s) manquante(s)`);
        detail.issues.slice(0, 4).forEach((i) => toast.error(i.message, { duration: 6000 }));
      } else {
        toast.error(detail || 'Erreur lors de l’émission');
      }
    } finally {
      setIssuing(false);
    }
  };

  const handleChangerStatut = async (newStatut) => {
    if (!invoice) return;
    try {
      await saleInvoicesAPI.changeStatus(id, { statut: newStatut });
      toast.success(`Statut → ${newStatut}`);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erreur changement statut');
    }
  };

  if (loading) return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', gap: 12, color: 'var(--text-muted)' }}>
      <div className="spinner" style={{ width: 24, height: 24 }}/> Chargement…
    </div>
  );

  const inv = invoice;
  const sc = statutColor(inv?.statut || form.statut);

  return (
    <div className="sid-page">
      {/* ── Barre supérieure ────────────────────────────────────────────── */}
      <div className="sid-topbar">
        <div className="sid-topbar-left">
          <button className="sid-back-btn" onClick={() => navigate('/sale-invoices')}>
            <ArrowLeft size={16}/>
          </button>
          <div>
            <div className="sid-ref">{isNew ? 'Nouvelle facture' : inv?.ref}</div>
            {!isNew && <div className="sid-ref-sub">{inv?.type_facture?.replace(/_/g, ' ')}</div>}
          </div>
          {!isNew && (
            <span className="sid-statut-badge" style={{ color: sc.color, background: sc.bg }}>
              {inv?.statut}
            </span>
          )}
        </div>

        {/* Boutons d'action */}
        <div className="sid-topbar-actions">
          {!isNew && (<>
            <button className="sid-action-btn" onClick={() => handleDownload('pdf')} disabled={downloading}>
              <Download size={13}/> PDF
            </button>
            {inv?.einvoice_format && inv.einvoice_format !== 'PDF_MENTIONS' && (
              <button className="sid-action-btn" onClick={() => handleDownload('xml')} disabled={downloading}>
                <Download size={13}/> XML
              </button>
            )}
            {inv?.is_draft && (
              <button className="sid-action-btn sid-action-btn--success" onClick={handleIssue} disabled={issuing}>
                {issuing ? 'Émission…' : 'Émettre'}
              </button>
            )}
            {inv?.statut !== 'ANNULEE' && (
              <button className="sid-action-btn sid-action-btn--danger" onClick={() => handleChangerStatut('ANNULEE')}>
                Annuler
              </button>
            )}
            {inv?.statut === 'VALIDEE' && (
              <button className="sid-action-btn sid-action-btn--primary" onClick={() => handleChangerStatut('PAYEE')}>
                Comptabiliser
              </button>
            )}
          </>)}
          {editMode ? (<>
            <button className="btn btn-secondary btn-sm" onClick={() => { setEditMode(false); if (!isNew) load(); }}>
              <X size={13}/> Annuler
            </button>
            <button className="btn btn-primary btn-sm" onClick={handleSave} disabled={saving}>
              <Save size={13}/> {saving ? 'Enregistrement…' : 'Enregistrer'}
            </button>
          </>) : !isNew && (
            <button className="btn btn-secondary btn-sm" onClick={() => setEditMode(true)}>
              <Pencil size={13}/> Modifier
            </button>
          )}
        </div>
      </div>

      {/* ── Tabs ────────────────────────────────────────────────────────── */}
      <div className="sid-tabs">
        {[['apercu','Aperçu'], ['paiement','Paiement'], ['attachements','Attachements']].map(([k,l]) => (
          <button key={k} className={`sid-tab ${activeTab === k ? 'sid-tab--active' : ''}`} onClick={() => setActiveTab(k)}>{l}</button>
        ))}
      </div>

      {/* ══════════════════════════════════════════════════════════════════ */}
      {/* APERÇU                                                            */}
      {/* ══════════════════════════════════════════════════════════════════ */}
      {activeTab === 'apercu' && (<>

        {/* EN-TÊTE */}
        <div className="card sid-card">
          <SLabel>EN-TÊTE</SLabel>
          <div className="sid-entete-grid">
            {/* Colonne gauche */}
            <div>
              {editMode ? (<>
                <EditField label="Type">
                  <select className="form-control" value={form.type_facture} onChange={e => setForm(f=>({...f,type_facture:e.target.value}))}>
                    {TYPES_INVOICE.map(t => <option key={t} value={t}>{t.replace(/_/g,' ')}</option>)}
                  </select>
                </EditField>
                <EditField label="Description">
                  <input className="form-control" value={form.description} onChange={e => setForm(f=>({...f,description:e.target.value}))} placeholder="Prestation de service…"/>
                </EditField>
                <EditField label="Émetteur">
                  <input className="form-control" value={form.emetteur} onChange={e => setForm(f=>({...f,emetteur:e.target.value}))} placeholder="WKEYS…"/>
                </EditField>
                <EditField label="Date">
                  <input type="date" className="form-control" value={form.date_facture} onChange={e => setForm(f=>({...f,date_facture:e.target.value}))}/>
                </EditField>
                <EditField label="Total HT">
                  <input type="number" className="form-control" value={fmtInput(totalHT)} readOnly disabled title="Calculé à partir des lignes"/>
                </EditField>
                <EditField label="Total TTC">
                  <input type="number" className="form-control" value={fmtInput(totalTTC)} readOnly disabled title="Calculé à partir des lignes"/>
                </EditField>
                <EditField label="Devise">
                  <select className="form-control" value={form.currency} onChange={e => setForm(f=>({...f,currency:e.target.value}))}>
                    {DEVISES.map(d => <option key={d.value} value={d.value}>{d.label}</option>)}
                  </select>
                </EditField>
                <EditField label="Affacturage">
                  <label style={{ display:'flex', alignItems:'center', gap:6, cursor:'pointer' }}>
                    <input type="checkbox" checked={form.affacturage} onChange={e => setForm(f=>({...f,affacturage:e.target.checked}))}/>
                    <span style={{ fontSize:'0.875rem' }}>{form.affacturage ? 'Oui' : 'Non'}</span>
                  </label>
                </EditField>
                <EditField label="Adresse sur la facture">
                  <input className="form-control" value={form.adresse_sur_facture} onChange={e => setForm(f=>({...f,adresse_sur_facture:e.target.value}))} placeholder="Adresse…"/>
                </EditField>
              </>) : (<>
                <Field label="Type">{inv?.type_facture?.replace(/_/g,' ')}</Field>
                <Field label="Description">{inv?.description}</Field>
                <Field label="Émetteur"><span style={{ color:'var(--orange-500)', fontWeight:600 }}>{inv?.emetteur}</span></Field>
                <Field label="Date">{fmtDate(inv?.date_facture)}</Field>
                <Field label="Total HT">{fmtNum(inv?.total_ht)} €</Field>
                <Field label="Total TTC">{fmtNum(inv?.total_ttc)} €</Field>
                <Field label="N° de référence">
                  {inv?.ref ? (
                    <span style={{ fontWeight:700 }}>{inv.ref}</span>
                  ) : defaults?.ref_preview ? (
                    <span style={{ fontWeight:700, opacity:0.7 }} title="Numéro définitif attribué à l'émission">
                      {defaults.ref_preview} <span style={{ fontWeight:400, fontSize:'0.75rem' }}>(prévisionnel)</span>
                    </span>
                  ) : '—'}
                </Field>
                <Field label="Affacturage">{inv?.affacturage ? 'Oui' : 'N'}</Field>
                <Field label="Adresse sur la facture">{inv?.adresse_sur_facture}</Field>
              </>)}
            </div>
            {/* Colonne droite */}
            <div>
              {editMode ? (<>
                <EditField label="Statut">
                  <select className="form-control" value={form.statut} onChange={e => setForm(f=>({...f,statut:e.target.value}))}>
                    {STATUTS_INVOICE.map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                </EditField>
                <EditField label="Message sur la facture">
                  <input className="form-control" value={form.message_facture} onChange={e => setForm(f=>({...f,message_facture:e.target.value}))} placeholder="Message…"/>
                </EditField>
                <EditField label="Destinataire">
                  <input className="form-control" value={form.destinataire} onChange={e => setForm(f=>({...f,destinataire:e.target.value}))} placeholder="Client…"/>
                </EditField>
                <EditField label="Net">
                  <input type="number" className="form-control" value={fmtInput(totalTTC)} readOnly disabled title="Calculé à partir des lignes"/>
                </EditField>
                <EditField label="Date d'échéance">
                  <input type="date" className="form-control" value={form.date_echeance} onChange={e => setForm(f=>({...f,date_echeance:e.target.value}))}/>
                </EditField>
                <EditField label="Date du paiement">
                  <input type="date" className="form-control" value={form.date_paiement} onChange={e => setForm(f=>({...f,date_paiement:e.target.value}))}/>
                </EditField>
              </>) : (<>
                <Field label="Statut">
                  <span style={{ padding:'3px 10px', borderRadius:12, fontSize:'0.75rem', fontWeight:700, color:sc.color, background:sc.bg }}>
                    {inv?.statut}
                  </span>
                </Field>
                <Field label="Message sur la facture">{inv?.message_facture}</Field>
                <Field label="Destinataire"><span style={{ color:'var(--orange-500)', fontWeight:600 }}>{inv?.destinataire}</span></Field>
                <Field label="Net">{fmtNum(inv?.net_a_payer)} {inv?.currency||'EUR'}</Field>
                <Field label="Date d'échéance">{fmtDate(inv?.date_echeance)}</Field>
                <Field label="Date du paiement">{fmtDate(inv?.date_paiement)}</Field>
              </>)}
            </div>
          </div>
        </div>

        {/* LIGNES DE FACTURATION */}
        <div className="card sid-card">
          <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:12 }}>
            <SLabel>LIGNES DE FACTURATION</SLabel>
            {(editMode || isNew) && (
              <button className="btn btn-primary btn-sm" onClick={() => { setLineForm({...EMPTY_LINE}); setEditLineId(null); setShowLineForm(true); }}>
                <Plus size={13}/> Ajouter une ligne
              </button>
            )}
          </div>

          {/* Formulaire ligne */}
          {showLineForm && (
            <div style={{ background:'var(--bg-elevated)', border:'1px solid var(--border)', borderRadius:8, padding:16, marginBottom:12, display:'grid', gridTemplateColumns:'2fr 1fr 1fr 100px 80px 80px auto', gap:10, alignItems:'end' }}>
              <div className="pd-field"><div className="pd-field-label">Description</div><input className="form-control" value={lineForm.description} onChange={e=>setLineForm(f=>({...f,description:e.target.value}))} placeholder="Prestation…"/></div>
              <div className="pd-field"><div className="pd-field-label">Catégorie</div><input className="form-control" value={lineForm.categorie} onChange={e=>setLineForm(f=>({...f,categorie:e.target.value}))} placeholder="Catégorie"/></div>
              <div className="pd-field"><div className="pd-field-label">Réf. projet</div><input className="form-control" value={lineForm.projet_ref} onChange={e=>setLineForm(f=>({...f,projet_ref:e.target.value}))} placeholder="Projet"/></div>
              <div className="pd-field"><div className="pd-field-label">PU HT</div><input type="number" className="form-control" value={lineForm.pu} onChange={e=>setLineForm(f=>({...f,pu:e.target.value}))} placeholder="0"/></div>
              <div className="pd-field"><div className="pd-field-label">Qté</div><input type="number" className="form-control" value={lineForm.quantite} onChange={e=>setLineForm(f=>({...f,quantite:e.target.value}))} min="0"/></div>
              <div className="pd-field"><div className="pd-field-label">% TVA</div><input type="number" className="form-control" value={lineForm.taux_tva} onChange={e=>setLineForm(f=>({...f,taux_tva:e.target.value}))} min="0" max="100"/></div>
              <div style={{ display:'flex', gap:6, paddingBottom:2 }}>
                <button className="btn btn-secondary btn-sm" onClick={() => { setShowLineForm(false); setEditLineId(null); }}><X size={13}/></button>
                <button className="btn btn-primary btn-sm" onClick={handleSaveLine} disabled={savingLine}><Check size={13}/></button>
              </div>
            </div>
          )}

          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Description</th>
                  <th>Catégorie</th>
                  <th>Projet</th>
                  <th style={{ textAlign:'right' }}>PU</th>
                  <th style={{ textAlign:'right' }}>PU TTC</th>
                  <th style={{ textAlign:'right' }}>Q</th>
                  <th style={{ textAlign:'right' }}>% TVA</th>
                  <th style={{ textAlign:'right' }}>TVA</th>
                  <th style={{ textAlign:'right' }}>Total</th>
                  <th style={{ textAlign:'right' }}>Total TTC</th>
                  {(editMode || isNew) && <th style={{ width:60 }}/>}
                </tr>
              </thead>
              <tbody>
                {lines.length === 0 ? (
                  <tr><td colSpan={11} style={{ textAlign:'center', padding:24, color:'var(--text-faint)', fontSize:'0.875rem' }}>Aucune ligne de facturation</td></tr>
                ) : lines.map((l, idx) => {
                  const c = computedLine(l);
                  const lineKey = l.id || idx;
                  return (
                    <tr key={lineKey}>
                      <td style={{ fontSize:'0.8125rem' }}>{l.description || '—'}</td>
                      <td style={{ fontSize:'0.8125rem', color:'var(--text-muted)' }}>{l.categorie || '—'}</td>
                      <td style={{ fontSize:'0.8125rem', color:'var(--orange-500)' }}>{l.projet_ref || '—'}</td>
                      <td style={{ textAlign:'right', fontVariantNumeric:'tabular-nums' }}>{fmtNum(c.pu)} €</td>
                      <td style={{ textAlign:'right', fontVariantNumeric:'tabular-nums' }}>{fmtNum(c.puTtc)} €</td>
                      <td style={{ textAlign:'right', fontVariantNumeric:'tabular-nums' }}>{Number(l.quantite||1)}</td>
                      <td style={{ textAlign:'right', fontVariantNumeric:'tabular-nums' }}>{Number(l.taux_tva||0)}</td>
                      <td style={{ textAlign:'right', fontVariantNumeric:'tabular-nums' }}>{fmtNum(c.tvaAmt)} €</td>
                      <td style={{ textAlign:'right', fontVariantNumeric:'tabular-nums' }}>{fmtNum(c.total)} €</td>
                      <td style={{ textAlign:'right', fontVariantNumeric:'tabular-nums', fontWeight:600 }}>{fmtNum(c.totalTtc)} €</td>
                      {(editMode || isNew) && <td>
                        <div style={{ display:'flex', gap:4 }}>
                          <button className="ts-btn-remove" onClick={() => startEditLine(l, l.id || idx)}><Pencil size={12}/></button>
                          <button className="ts-btn-remove" onClick={() => handleDeleteLine(l.id, idx)}><Trash2 size={12}/></button>
                        </div>
                      </td>}
                    </tr>
                  );
                })}
              </tbody>
              {lines.length > 0 && (
                <tfoot>
                  <tr>
                    <td colSpan={8} style={{ textAlign:'right', fontWeight:600, fontSize:'0.8125rem', paddingRight:8, color:'var(--text-muted)' }}>Total HT</td>
                    <td style={{ textAlign:'right', fontVariantNumeric:'tabular-nums', fontWeight:700 }}>{fmtNum(totalHT)} €</td>
                    <td/>
                    {(editMode || isNew) && <td/>}
                  </tr>
                  <tr>
                    <td colSpan={8} style={{ textAlign:'right', fontWeight:600, fontSize:'0.8125rem', paddingRight:8, color:'var(--text-muted)' }}>TVA</td>
                    <td style={{ textAlign:'right', fontVariantNumeric:'tabular-nums', fontWeight:700 }}>{fmtNum(totalTVA)} €</td>
                    <td/>
                    {(editMode || isNew) && <td/>}
                  </tr>
                  <tr>
                    <td colSpan={8} style={{ textAlign:'right', fontWeight:700, fontSize:'0.875rem', paddingRight:8 }}>Total TTC</td>
                    <td style={{ textAlign:'right', fontVariantNumeric:'tabular-nums', fontWeight:800, fontSize:'0.95rem', color:'var(--orange-500)' }}>{fmtNum(totalTTC)} €</td>
                    <td/>
                    {(editMode || isNew) && <td/>}
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        </div>

        {/* 4 blocs info en 2 colonnes */}
        <div className="sid-info-grid">

          {/* INFORMATIONS SUPPLÉMENTAIRES */}
          <div className="card sid-card">
            <div className="sid-collapsible-header">
              <SLabel>INFORMATIONS SUPPLÉMENTAIRES</SLabel>
              <div style={{ display:'flex', gap:6 }}>
                <button className="sid-icon-btn"><Pencil size={13}/></button>
                <button className="sid-icon-btn"><RefreshCw size={13}/></button>
              </div>
            </div>
            {editMode ? (
              <EditField label="Code client">
                <input className="form-control" value={form.code_client} onChange={e=>setForm(f=>({...f,code_client:e.target.value}))} placeholder="CODE-001"/>
              </EditField>
            ) : (
              <Field label="Code client">{inv?.code_client}</Field>
            )}
          </div>

          {/* RÈGLEMENT ATTENDU LE */}
          <div className="card sid-card">
            <div className="sid-collapsible-header">
              <SLabel>RÈGLEMENT ATTENDU LE</SLabel>
            </div>
            {editMode ? (<>
              <EditField label="Date">
                <input type="date" className="form-control" value={form.date_reglement} onChange={e=>setForm(f=>({...f,date_reglement:e.target.value}))}/>
              </EditField>
              <EditField label="Méthode">
                <input className="form-control" value={form.methode_reglement} onChange={e=>setForm(f=>({...f,methode_reglement:e.target.value}))} placeholder="Virement…"/>
              </EditField>
            </>) : (<>
              <Field label="Date">{fmtDate(inv?.date_reglement)}</Field>
              <Field label="Méthode">{inv?.methode_reglement}</Field>
            </>)}
          </div>

          {/* INFORMATIONS PROJET FACTURE */}
          <div className="card sid-card">
            <div className="sid-collapsible-header">
              <SLabel>INFORMATIONS PROJET FACTURE</SLabel>
              <div style={{ display:'flex', gap:6 }}>
                <button className="sid-icon-btn"><Pencil size={13}/></button>
                <button className="sid-icon-btn"><RefreshCw size={13}/></button>
              </div>
            </div>
            {editMode ? (<>
              <EditField label="Centre de Coût">
                <input className="form-control" value={form.centre_cout} onChange={e=>setForm(f=>({...f,centre_cout:e.target.value}))} placeholder="Centre…"/>
              </EditField>
              <EditField label="Bon de commande">
                <input className="form-control" value={form.bon_commande} onChange={e=>setForm(f=>({...f,bon_commande:e.target.value}))} placeholder="BC-001"/>
              </EditField>
              <EditField label="N° de contrat">
                <input className="form-control" value={form.n_contrat_ref} onChange={e=>setForm(f=>({...f,n_contrat_ref:e.target.value}))} placeholder="CT-001"/>
              </EditField>
            </>) : (<>
              <Field label="Centre de Coût">{inv?.centre_cout}</Field>
              <Field label="Bon de commande">{inv?.bon_commande}</Field>
              <Field label="N° de contrat">{inv?.n_contrat_ref}</Field>
            </>)}
          </div>

          {/* INFORMATIONS DEVIS */}
          <div className="card sid-card">
            <div className="sid-collapsible-header">
              <SLabel>INFORMATIONS DEVIS</SLabel>
              <div style={{ display:'flex', gap:6 }}>
                <button className="sid-icon-btn"><Pencil size={13}/></button>
                <button className="sid-icon-btn"><RefreshCw size={13}/></button>
              </div>
            </div>
            {editMode ? (
              <EditField label="N° chrono devis">
                <input className="form-control" value={form.n_chrono_devis} onChange={e=>setForm(f=>({...f,n_chrono_devis:e.target.value}))} placeholder="DEV-001"/>
              </EditField>
            ) : (
              <Field label="N° chrono devis">{inv?.n_chrono_devis}</Field>
            )}
          </div>

          {/* CHARGÉ D'AFFAIRE */}
          <div className="card sid-card">
            <SLabel>CHARGÉ D'AFFAIRE</SLabel>
            {editMode ? (<>
              <EditField label="Chargé d'affaires">
                <input className="form-control" value={form.charge_affaires} onChange={e=>setForm(f=>({...f,charge_affaires:e.target.value}))} placeholder="Nom du chargé"/>
              </EditField>
              <EditField label="% Charges d'affaires">
                <input type="number" className="form-control" value={form.charges_affaires_pct} onChange={e=>setForm(f=>({...f,charges_affaires_pct:e.target.value}))} placeholder="0" min="0" max="100"/>
              </EditField>
            </>) : (<>
              <Field label="Chargé d'affaires">{inv?.charge_affaires}</Field>
              <Field label="% Charges d'affaires">{inv?.charges_affaires_pct != null ? `${inv.charges_affaires_pct}%` : null}</Field>
            </>)}
          </div>

          {/* ENTITÉ COMMERCIALE */}
          <div className="card sid-card">
            <SLabel>ENTITÉ COMMERCIALE</SLabel>
            {editMode ? (
              <EditField label="Entité commerciale">
                <input className="form-control" value={form.entite_commerciale} onChange={e=>setForm(f=>({...f,entite_commerciale:e.target.value}))} placeholder="WKEYS…"/>
              </EditField>
            ) : (
              <Field label="Entité commerciale"><span style={{ color:'var(--orange-500)', fontWeight:600 }}>{inv?.entite_commerciale}</span></Field>
            )}
          </div>
        </div>

        {/* RELANCES */}
        <div className="card sid-card">
          <SLabel>RELANCES</SLabel>
          <div className="table-wrap">
            <table>
              <thead><tr><th>Niveau de relance</th><th>Date de relance</th><th>Statut</th><th>PDF</th><th>Envoyer par email</th></tr></thead>
              <tbody>
                <tr><td colSpan={5} style={{ textAlign:'center', padding:24, color:'var(--text-faint)', fontSize:'0.875rem' }}>Aucune donnée trouvée</td></tr>
              </tbody>
            </table>
          </div>
        </div>

        {/* RÔLES */}
        <div className="card sid-card">
          <SLabel>RÔLES</SLabel>
          <div className="table-wrap">
            <table>
              <thead><tr><th>ID Acteur</th><th>Nom</th><th>Rôle</th><th>Pourcentage</th><th>Effectué (Coût × Nomb.)</th></tr></thead>
              <tbody>
                {inv?.emetteur ? (
                  <tr>
                    <td style={{ fontFamily:'monospace', fontSize:'0.8125rem', color:'var(--text-faint)' }}>—</td>
                    <td style={{ fontWeight:600, color:'var(--orange-500)' }}>{inv.emetteur}</td>
                    <td>Donneur</td>
                    <td>—</td>
                    <td>—</td>
                  </tr>
                ) : (
                  <tr><td colSpan={5} style={{ textAlign:'center', padding:24, color:'var(--text-faint)', fontSize:'0.875rem' }}>Aucune donnée trouvée</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* COMMENTAIRES */}
        <div className="card sid-card">
          <SLabel>COMMENTAIRES</SLabel>
          <div style={{ padding:'4px 0 12px' }}>
            <textarea className="form-control" rows={3} placeholder="Commentaire…" style={{ resize:'vertical' }}/>
            <button className="btn btn-primary btn-sm" style={{ marginTop:8 }}
              onClick={() => toast('Commentaire — disponible prochainement')}>Ajouter</button>
          </div>
        </div>

        {/* RIB AFFICHÉ SUR LA FACTURE */}
        <div className="card sid-card">
          <SLabel>RIB AFFICHÉ SUR LA FACTURE</SLabel>
          {editMode ? (
            <EditField label="Méthode de paiement">
              <input className="form-control" value={form.methode_paiement_rib} onChange={e=>setForm(f=>({...f,methode_paiement_rib:e.target.value}))} placeholder="Virement IBAN…"/>
            </EditField>
          ) : (
            <Field label="Méthode de paiement">{inv?.methode_paiement_rib}</Field>
          )}
        </div>

        {/* TABLEAU DE TVA */}
        {lines.length > 0 && (
          <div className="card sid-card">
            <SLabel>TABLEAU DE TVA</SLabel>
            <div className="table-wrap">
              <table>
                <thead><tr><th>% TVA</th><th style={{ textAlign:'right' }}>Total HT</th><th style={{ textAlign:'right' }}>Total TTC</th><th style={{ textAlign:'right' }}>TVA</th></tr></thead>
                <tbody>
                  {Array.from(new Set(lines.map(l => Number(l.taux_tva || 0)))).sort().map(taux => {
                    const filtered = lines.filter(l => Number(l.taux_tva || 0) === taux);
                    const ht  = filtered.reduce((s, l) => s + computedLine(l).total, 0);
                    const tva = filtered.reduce((s, l) => s + computedLine(l).tvaAmt, 0);
                    return (
                      <tr key={taux}>
                        <td>{taux}</td>
                        <td style={{ textAlign:'right', fontVariantNumeric:'tabular-nums' }}>{fmtNum(ht)} €</td>
                        <td style={{ textAlign:'right', fontVariantNumeric:'tabular-nums' }}>{fmtNum(ht + tva)} €</td>
                        <td style={{ textAlign:'right', fontVariantNumeric:'tabular-nums' }}>{fmtNum(tva)} €</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* STATUT (historique) */}
        {!isNew && inv?.history?.length > 0 && (
          <div className="card sid-card">
            <SLabel>STATUT</SLabel>
            <div className="table-wrap">
              <table>
                <thead><tr><th>Date du statut</th><th>Statut</th><th>Par l'utilisateur</th></tr></thead>
                <tbody>
                  {[...inv.history].reverse().map(h => {
                    const hsc = statutColor(h.statut);
                    return (
                      <tr key={h.id}>
                        <td style={{ fontSize:'0.8125rem', fontVariantNumeric:'tabular-nums', whiteSpace:'nowrap' }}>{fmtDateTime(h.created_at)}</td>
                        <td><span style={{ padding:'3px 10px', borderRadius:12, fontSize:'0.72rem', fontWeight:700, color:hsc.color, background:hsc.bg }}>{h.statut}</span></td>
                        <td style={{ fontSize:'0.8125rem', color:'var(--text-muted)' }}>{h.user_nom || '—'}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* PAIEMENTS ALLOUÉS */}
        <div className="card sid-card">
          <SLabel>PAIEMENTS ALLOUÉS {fmtNum(inv?.montant_paye)} € EN ATTENTE {fmtNum(inv?.montant_impaye)} €</SLabel>
          <div className="table-wrap">
            <table>
              <thead><tr><th>Réf. ligne de la facture</th><th>Réf. produit</th><th>Description</th><th style={{ textAlign:'right' }}>Total</th><th>Réf.</th><th style={{ textAlign:'right' }}>Montant alloué</th></tr></thead>
              <tbody><tr><td colSpan={6} style={{ textAlign:'center', padding:24, color:'var(--text-faint)', fontSize:'0.875rem' }}>Aucune donnée trouvée</td></tr></tbody>
            </table>
          </div>
        </div>

        {/* CONTRATS */}
        <div className="card sid-card">
          <SLabel>CONTRATS</SLabel>
          <div className="table-wrap">
            <table>
              <thead><tr><th>Date du contrat</th><th>Réf.</th><th style={{ textAlign:'right' }}>Total général</th><th>Statut</th></tr></thead>
              <tbody>
                {inv?.contrat_id ? (
                  <tr>
                    <td style={{ fontSize:'0.8125rem' }}>—</td>
                    <td style={{ fontSize:'0.8125rem', color:'var(--orange-500)' }}>{inv.contrat_id}</td>
                    <td style={{ textAlign:'right' }}>—</td>
                    <td>—</td>
                  </tr>
                ) : (
                  <tr><td colSpan={4} style={{ textAlign:'center', padding:24, color:'var(--text-faint)', fontSize:'0.875rem' }}>Aucun contrat lié</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

      </>)}

      {/* ══════════════════════════════════════════════════════════════════ */}
      {/* PAIEMENT                                                          */}
      {/* ══════════════════════════════════════════════════════════════════ */}
      {activeTab === 'paiement' && (
        <div className="card sid-card">
          <SLabel>PAIEMENT</SLabel>
          <div className="sid-info-grid">
            <div>
              {editMode ? (<>
                <EditField label="Montant payé">
                  <input type="number" className="form-control" value={form.montant_paye} onChange={e=>setForm(f=>({...f,montant_paye:e.target.value}))} placeholder="0"/>
                </EditField>
                <EditField label="Date du paiement">
                  <input type="date" className="form-control" value={form.date_paiement} onChange={e=>setForm(f=>({...f,date_paiement:e.target.value}))}/>
                </EditField>
              </>) : (<>
                <Field label="Montant payé">{fmtNum(inv?.montant_paye)} €</Field>
                <Field label="Montant impayé">{fmtNum(inv?.montant_impaye)} €</Field>
                <Field label="Date du paiement">{fmtDate(inv?.date_paiement)}</Field>
              </>)}
            </div>
          </div>
          {editMode && (
            <div style={{ marginTop:12, display:'flex', justifyContent:'flex-end', gap:8 }}>
              <button className="btn btn-primary btn-sm" onClick={handleSave} disabled={saving}>
                <Save size={13}/> Enregistrer le paiement
              </button>
            </div>
          )}
        </div>
      )}

      {/* ══════════════════════════════════════════════════════════════════ */}
      {/* ATTACHEMENTS                                                       */}
      {/* ══════════════════════════════════════════════════════════════════ */}
      {activeTab === 'attachements' && (
        <div className="card sid-card" style={{ padding:40, textAlign:'center', color:'var(--text-faint)' }}>
          <AlertCircle size={28} style={{ opacity:0.4, marginBottom:8, display:'block', margin:'0 auto 8px' }}/>
          <div style={{ fontSize:'0.875rem' }}>Les pièces jointes seront disponibles prochainement.</div>
        </div>
      )}
    </div>
  );
}
